/*
 * Kitchen Display API client.
 *
 * Authenticated endpoints used by the Mətbəx (Mutfak) screen. Backend
 * exposes the active tickets per kitchen and accepts status updates that
 * cascade to all items in a given order.
 *
 * Endpoints (sanctum bearer auth + has.restaurant gate):
 *   GET    /api/kitchens/{kitchenId}/display
 *      → Active tickets (pending + preparing + ready). Each ticket maps to
 *        one order; overall_status is derived per-order.
 *
 *   PATCH  /api/kitchens/{kitchenId}/orders/{orderId}/status
 *      Body: { status: 'preparing' | 'ready' | 'cancelled' | 'pending' }
 *      → Updates every kitchen item of the order. When status === 'ready'
 *        the backend fans out a notification to the POS.
 */

import axios from "axios";
import { base_url } from "./index";
import { attachPosGateway } from "./pos/install";

// ---------- Domain types ----------

// `served` = ofisant təhvil aldı. Bu statusa keçən ticket aktiv lövhədən
// çıxır və arxivə düşür.
export type KitchenOrderStatus =
  | "pending"
  | "preparing"
  | "ready"
  | "served"
  | "cancelled";
export type TicketChannel = "dine-in" | "takeaway" | "delivery";

export interface KitchenTicketItem {
  id: number | string;
  name: string;
  quantity: number;
  notes?: string | null;
  status?: KitchenOrderStatus;
}

// POS rejimində sifariş id-si string-dir (`ord_01M0...`), cloud-da numerik.
// Hər ikisi dəstəklənir.
export type OrderRef = number | string;

export interface KitchenTicket {
  id: OrderRef;
  order_id: OrderRef;
  order_number: string;
  type: TicketChannel;
  table?: string | null;
  overall_status: KitchenOrderStatus;
  items: KitchenTicketItem[];
  created_at: string;
  /** Təhvil anı (ISO). Yalnız arxivdə dolu olur. */
  served_at?: string | null;
}

export interface KitchenDisplayResponse {
  /** pos_local `kit_1` string, cloud numerik id qaytarır. */
  kitchen?: { id: OrderRef; name?: string | null; color?: string | null };
  tickets: KitchenTicket[];
}

export interface KitchenSummary {
  id: OrderRef;
  name?: string;
  color?: string | null;
  /** Mətbəxə printer bağlanmayıbsa fiş çıxmır — UI xəbərdarlıq göstərir. */
  has_printer?: boolean;
}

export interface KitchenStatusUpdateResponse {
  message?: string;
  ticket?: KitchenTicket;
}

// ---------- HTTP client ----------

const kitchenClient = axios.create({
  baseURL: base_url,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  timeout: 15000,
});

// POS rejimində mətbəx sorğuları pos_local-a getməlidir. Öz axios
// instansiyamız olduğu üçün qlobal interceptor buraya düşmür — əl ilə qoşuruq.
// Bu olmadan mətbəx ekranı cloud KDS-ə baxırdı və POS sifarişlərini görmürdü.
attachPosGateway(kitchenClient);

kitchenClient.interceptors.request.use((config) => {
  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;
  if (token && config.headers) {
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------- Defensive normalization ----------
//
// Backend MAY ship slightly different shapes depending on the
// dine-in / takeaway / delivery channel. We coerce everything into the
// `KitchenTicket` contract so the UI never has to defensive-map.

const normalizeChannel = (raw: any): TicketChannel => {
  const v = String(raw ?? "").toLowerCase();
  if (v.includes("takeaway") || v.includes("paket")) return "takeaway";
  if (v.includes("delivery") || v.includes("kurye") || v.includes("gel"))
    return "delivery";
  return "dine-in";
};

const normalizeStatus = (raw: any): KitchenOrderStatus => {
  const v = String(raw ?? "").toLowerCase();
  if (v === "preparing" || v === "in_progress") return "preparing";
  if (v === "ready" || v === "done" || v === "completed") return "ready";
  if (v === "served" || v === "delivered" || v === "picked_up") return "served";
  if (v === "cancelled" || v === "canceled" || v === "rejected")
    return "cancelled";
  return "pending";
};

const readArray = (...values: any[]): any[] => {
  for (const value of values) {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object" && Array.isArray(value.data))
      return value.data;
  }
  return [];
};

const normalizeTicket = (raw: any, idx: number): KitchenTicket => {
  const order = raw?.order && typeof raw.order === "object" ? raw.order : {};
  const table = raw?.table && typeof raw.table === "object" ? raw.table : null;
  const items = readArray(
    raw?.items,
    raw?.order_items,
    raw?.kitchen_items,
    raw?.stocks,
    order?.items,
    order?.stocks,
  );
  // Cloud numerik id verir, pos_local isə `ord_01M0...` string. Numerikə
  // çevrilə bilirsə çeviririk (köhnə davranış), yoxsa string olduğu kimi qalır —
  // əks halda POS ticket-ləri NaN → 0 olub bir-birinə qarışırdı.
  const asRef = (value: unknown): OrderRef | null => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) && String(value).trim() !== '' ? n : String(value);
  };
  const orderId = asRef(raw?.order_id) ?? asRef(order?.id) ?? asRef(raw?.id) ?? 0;
  const ticketId = asRef(raw?.id) ?? asRef(raw?.ticket_id) ?? orderId ?? idx + 1;

  return {
    id: ticketId,
    order_id: orderId,
    order_number: String(
      ((raw?.order_number ?? order?.order_number ?? orderId) || raw?.id) ??
        `#${idx + 1}`,
    ),
    type: normalizeChannel(
      raw?.type ?? raw?.channel ?? order?.type ?? order?.source,
    ),
    table:
      table?.name ?? raw?.table_name ?? raw?.table ?? order?.table_name ?? null,
    overall_status: normalizeStatus(
      raw?.overall_status ?? raw?.status ?? order?.status,
    ),
    items: items.map((it: any, i: number) => {
      const pivot = it?.pivot && typeof it.pivot === "object" ? it.pivot : {};
      const stock = it?.stock && typeof it.stock === "object" ? it.stock : {};
      return {
        id: it?.id ?? pivot?.id ?? `${i}`,
        name: String(
          it?.name ??
            stock?.name ??
            it?.product_name ??
            it?.stock_name ??
            "Ürün",
        ),
        quantity: Number(pivot?.quantity ?? it?.quantity ?? it?.qty ?? 1) || 1,
        notes:
          typeof it?.notes === "string"
            ? it.notes
            : (it?.note ?? pivot?.note ?? null),
        // pos_local `kitchen_status` göndərir, cloud `status`/pivot.status.
        status:
          it?.kitchen_status || it?.status || pivot?.status
            ? normalizeStatus(it.kitchen_status ?? it.status ?? pivot.status)
            : undefined,
      };
    }),
    served_at: raw?.served_at ?? null,
    // `sent_at` = mətbəxə göndərilmə anı (pos_local). Bu olmadan hər ticket
    // "indi" görünürdü və gecikmə saymırdı.
    created_at: String(
      raw?.sent_at ??
        raw?.created_at ??
        raw?.createdAt ??
        order?.created_at ??
        new Date().toISOString(),
    ),
  };
};

/**
 * Qısa sifariş istinadı. pos_local id-si tam ULID-dir
 * (`ord_01M0HW82GMN7HX7TGR1CPEEEKF`) və başlığa sığmır — son 4 simvol
 * fərqləndirmək üçün kifayətdir. Cloud numerik id olduğu kimi qalır.
 *
 * Mətbəx lövhəsi və Bildirişlər səhifəsi eyni etiketi göstərməlidir ki, işçi
 * iki ekranı tutuşdura bilsin — ona görə burada, API qatında yaşayır.
 */
export const shortRef = (ref: OrderRef): string => {
  const s = String(ref);
  if (/^\d+$/.test(s)) return s;
  return s.replace(/^ord_/, "").slice(-4).toUpperCase();
};

const unwrapKitchenList = (payload: any): any[] =>
  readArray(payload?.kitchens, payload?.data?.kitchens, payload?.data, payload);

// ---------- API calls ----------

/**
 * Fetch all active tickets (pending + preparing + ready) for a kitchen.
 *
 * @param kitchenId  The id of the kitchen to query.
 * @param signal     Optional AbortSignal for cancellation.
 */
export async function getKitchenDisplay(
  kitchenId: OrderRef,
  signal?: AbortSignal,
): Promise<KitchenDisplayResponse> {
  const res = await kitchenClient.get(`/kitchens/${kitchenId}/display`, {
    signal,
  });
  const data = res.data ?? {};
  const rawTickets = readArray(
    data?.tickets,
    data?.data?.tickets,
    data?.data?.data,
    data?.orders,
    data?.data?.orders,
    data,
  );

  return {
    kitchen: data?.kitchen ?? data?.data?.kitchen,
    tickets: rawTickets.map((t, i) => normalizeTicket(t, i)),
  };
}

export interface KitchenArchiveResponse {
  kitchen?: { id: OrderRef; name?: string | null };
  range: { from: number; to: number };
  tickets: KitchenTicket[];
}

/**
 * Mətbəx arxivi — ofisantın təhvil aldığı ticket-lər, tarix aralığı ilə.
 *
 * POS rejimində pos_local-dan gəlir (bütün yerli tarixçə). Aralıq yerli
 * saxlama pəncərəsindən kənardırsa cloud API-yə keçmək lazımdır — hazırda
 * yerli məlumat qaytarılır.
 */
export async function getKitchenArchive(
  kitchenId: OrderRef,
  fromMs: number,
  toMs: number,
  signal?: AbortSignal,
): Promise<KitchenArchiveResponse> {
  const res = await kitchenClient.get(`/kitchens/${kitchenId}/archive`, {
    params: { from: fromMs, to: toMs },
    signal,
  });
  const data = res.data ?? {};
  const rawTickets = readArray(data?.tickets, data?.data?.tickets, data);
  return {
    kitchen: data?.kitchen ?? data?.data?.kitchen,
    range: data?.range ?? { from: fromMs, to: toMs },
    tickets: rawTickets.map((t, i) => normalizeTicket(t, i)),
  };
}

export async function getKitchens(
  signal?: AbortSignal,
): Promise<KitchenSummary[]> {
  const res = await kitchenClient.get("/kitchens", { signal });
  // `color` və `has_printer` KitchenPicker üçün lazımdır — əvvəl atılırdı.
  return unwrapKitchenList(res.data)
    .map((k: any) => ({
      id: Number(k?.id),
      name: k?.name,
      color: k?.color ?? null,
      has_printer: k?.has_printer ?? undefined,
    }))
    .filter((k: KitchenSummary) => Number.isFinite(Number(k.id)) && Number(k.id) > 0);
}

/**
 * Update the status of every kitchen item of the given order.
 * When status === 'ready' the backend pushes a notification to the POS.
 */
export async function updateKitchenOrderStatus(
  kitchenId: OrderRef,
  orderId: OrderRef,
  status: KitchenOrderStatus,
  signal?: AbortSignal,
): Promise<KitchenStatusUpdateResponse> {
  const res = await kitchenClient.patch<KitchenStatusUpdateResponse>(
    `/kitchens/${kitchenId}/orders/${orderId}/status`,
    { status },
    { signal },
  );
  return res.data;
}
